/** FELURILE DE CAMERĂ ale unui tronson — cum se numesc și care se pot alege.
 *
 *  NUMELE VINE DIN LIMBĂ, nu din `generated/kinds::LABEL`. Tabelul ăla e
 *  vocabularul canonic, cu eticheta ROMÂNEASCĂ a fiecărui fel — bun ca plasă
 *  pentru un fel pe care nu-l cunoaște nimeni, dar cine citește platforma în
 *  engleză vedea «Dormitor» scris pe planșă lângă «Save the draft». Numele
 *  unei camere e text de interfață ca oricare altul, deci stă în catalogul de
 *  traduceri și cade pe `LABEL` doar pentru un fel fără cheie.
 *
 *  LISTA DE ALES e mai scurtă decât vocabularul: pe un tronson se pun camerele
 *  unei locuințe și ale circulației ei, nu locuri de parcare. Ordinea e cea în
 *  care le caută cineva — cele de zi, cele de noapte, cele ude, circulațiile,
 *  afară, anexele.
 *
 *  S-A LUNGIT PENTRU CASE (22.09.2026). Cât timp catalogul avea numai module de
 *  bară, lista de 15 ajungea: un apartament n-are birou, cămară, dressing sau
 *  garaj. Un plan de locuință individuală le are, iar un fel care lipsește din
 *  listă nu e o lipsă de comoditate — e informație aruncată: camera aceea se
 *  face `room`, adică «nu se știe ce e», deși pe planșă scria «Birou».
 *
 *  CE E AICI E ȘI ÎN VOCABULARUL CANONIC (`generated/kinds.ts`), fiindcă altfel
 *  felul ales din interfață n-ar exista pentru niciun motor. Așa a stat `tech`
 *  până azi: se putea alege și nu era în tabel. Cine adaugă un fel aici îl
 *  adaugă întâi în `canonical/src/canonical_model/kinds.py` și regenerează.
 *  Proba care ține legătura: `feluri.test.ts`.
 */
import { LABEL } from "../generated/kinds";
import { inToateLimbile, t } from "../../i18n";
import { ro } from "../../i18n/ro";
import type { Cheie } from "../../i18n";

/** Felurile pe care le poate lua o cameră de tronson, în ordinea paletei. */
export const FELURI_CAMERA = [
  // ziua
  "living", "kitchen", "office",
  // noaptea
  "bedroom", "dressing",
  // udele
  "bath", "bath2", "wc", "shower",
  // circulațiile
  "hall", "hol", "sas",
  // afară
  "balcony", "terasa", "terasa_circulabila",
  // anexele
  // GOSPODĂRIA DE APĂ e un fel canonic vechi (`kinds::gospodarie`), venit din
  // planșele de parcare, dar care lipsea din lista editorului: exista în
  // document și se putea desena, numai că nu se putea ALEGE (21.10.2026).
  "store", "pantry", "garage", "boxe", "ghena", "tech", "gospodarie",
  // și zona prin care iese fumul la incendiu — v. `kinds::desfumare`
  "desfumare",
  // și cea despre care nu se știe nimic
  "room",
] as const;

/** FELUL UNEI CAMERE DESPRE CARE NU SE ȘTIE NIMIC.
 *
 *  A fost multă vreme «other», și «other» nu e un fel: nu există în vocabularul
 *  canonic (v. `generated/kinds::SpaceLikeKind`), deci pleca mai departe în
 *  document un cuvânt pe care niciun motor nu-l cunoaște, scris pe planșă
 *  «Cameră» ca și cum ar fi fost unul. `room` E felul canonic pentru exact
 *  asta — o încăpere de locuit căreia nu i s-a spus încă ce e —, deci se
 *  alege din listă ca oricare altul și nu mai e o gaură în vocabular.
 *
 *  ȘI SE NUMEȘTE AȘA PE PLANȘĂ: «Cameră necunoscută», nu «Cameră». Un spațiu
 *  închis cu ziduri iese cameră oricum (v. `camere::refacute`) — dar felul nu
 *  se vede din formă, deci numele lui trebuie să spună chiar asta, ca omul să
 *  știe că mai are ceva de configurat acolo. Se dă clic pe ea și i se alege
 *  felul din panou, ca oricărei camere. */
export const FEL_IMPLICIT = "room";

const CHEIE: Record<string, Cheie> = Object.fromEntries(
  [...FELURI_CAMERA, "dormitor", "scara", "lift"].map(
    (f) => [f, `fel.camera.${f}` as Cheie]));

/** Numele unui fel de cameră, în limba curentă.
 *
 *  DOUĂ VOCABULARE, NU UNUL ȘI O PLASĂ ROMÂNEASCĂ. `CHEIE` de mai sus acoperă
 *  cele 25 de feluri ale editorului de tronson; documentele poartă însă și
 *  felurile venite din planșele de parcare (`bazin`, `fdcp`, `te`, `ecs`,
 *  `te_desfumare`, `gospodarie`) și pe cele de adăpost (`ala*`). Pentru ele se
 *  cădea pe `LABEL`, tabelul canonic — care e ROMÂNESC, deci în interfața
 *  englezească scria «Bazin retenție», «Gospodărie apă», «Adăpost ALA»
 *  (12.10.2026: «only romanian room names on the romanian setting and only
 *  english on the english setting»).
 *
 *  Al doilea vocabular există de mult și le are pe toate, în amândouă limbile:
 *  `kind.<fel>` (v. `viewer/vocabular`), ținut complet de proba lui. Deci se
 *  cade pe el, nu pe etichetă; `LABEL` rămâne numai ca să se știe CE feluri
 *  există, nu cum se spun. Codul brut se întoarce doar pentru un fel pe care
 *  nu-l cunoaște niciunul — adică nimic din ce scrie cineva azi. */
export function numeleFelului(kind: string): string {
  const cheie = CHEIE[kind];
  if (cheie) return t(cheie);
  return kind in LABEL ? t(`kind.${kind}` as Cheie) : kind;
}

/** NUMELE SCURT al unui fel, pentru camerele în care cel lung nu încape.
 *
 *  O zonă de desfumare e un colț de 0,7 m² în ungherul casei scării, iar
 *  «ZONĂ DE DESFUMARE» are șaptesprezece litere: la mărirea de lucru numele se
 *  strânge sub pragul de citit, deci eticheta dispărea cu totul și zona rămânea
 *  o bucată de planșă pe care n-o vede nimeni. Cerut să scrie ceva pe ea
 *  (14.10.2026: «maybe just write zona desfumare or smth. No hash needed»).
 *
 *  `null` pentru felurile care n-au nevoie — adică aproape toate. Un nume scurt
 *  pus unde cel lung oricum încape ar fi o scurtare degeaba. */
export function numeScurtCamerei(kind: string): string | null {
  const cheie = `fel.scurt.${kind}` as Cheie;
  return cheie in ro ? t(cheie) : null;
}

/** Textul, fără diacritice și fără majuscule — ca să se poată recunoaște
 *  «Bucatarie» scris de cititor în «Bucătărie» scris în catalog. */
const normal = (s: string) =>
  s.normalize("NFD").replace(/[̀-ͯ]/g, "")
    .toLowerCase().replace(/\s+/g, " ").trim();

/** NUMELE PE CARE LE-A SCRIS CITITORUL ÎN DOCUMENTE, și care nu mai sunt în
 *  niciun catalog.
 *
 *  Documentele poartă numele camerei așa cum l-a scris cititorul la import —
 *  în română, fiindcă în română e promptul lui. Cele mai multe se regăsesc în
 *  catalog («Dormitor», «Baie», «Balcon»), dar trei nu: `living` se numea
 *  «Living» până la 12.10.2026, când a devenit «Camera de zi»; casa scării și
 *  holul comun au în catalog alt cuvânt decât cel scris pe planșe. Fără ele,
 *  eticheta nu se poate recunoaște ca fiind chiar numele felului, deci rămâne
 *  scrisă românește și pe ecranul englezesc. Tabelul e pentru documentele DEJA
 *  SCRISE; nu are de ce să crească. */
const NUME_SCRISE: Record<string, string[]> = {
  living: ["Living"],
  scara: ["Casa scarii", "Casa scării", "Scara"],
  hol: ["Hol comun", "Hol"],
  hall: ["Hol"],
  bedroom: ["Camera", "Cameră"],
  room: ["Camera", "Cameră"],
};

/** Toate numele sub care se poate întâlni un fel, în orice limbă. */
function numeleCunoscute(kind: string): Set<string> {
  let s = _cunoscute.get(kind);
  if (!s) {
    s = new Set<string>();
    const cheie = CHEIE[kind];
    for (const n of cheie ? inToateLimbile(cheie) : []) s.add(normal(n));
    if (kind in LABEL) {
      for (const n of inToateLimbile(`kind.${kind}` as Cheie)) s.add(normal(n));
    }
    for (const n of NUME_SCRISE[kind] ?? []) s.add(normal(n));
    _cunoscute.set(kind, s);
  }
  return s;
}
const _cunoscute = new Map<string, Set<string>>();

/** Felul al cărui nume e textul ăsta, dacă e al vreunuia. */
function felulDupaNume(text: string): string | null {
  const n = normal(text);
  for (const k of Object.keys(CHEIE)) if (numeleCunoscute(k).has(n)) return k;
  return null;
}

/** NUMELE UNEI CAMERE: eticheta ei, dar spusă în limba ecranului.
 *
 *  DE CE NU AJUNGE `numeleFelului`. Documentul poartă, pe fiecare cameră, o
 *  etichetă scrisă de cititor la import — în română —, iar desenul o scria pe
 *  ea, nu numele felului: ea e singurul loc unde pot sta lucruri pe care felul
 *  nu le știe («Living + bucătărie», «Dormitor 2»). Dar atunci redenumirea
 *  felului nu se vedea nicăieri (`living` a ajuns «Camera de zi» pe
 *  12.10.2026, și pe toate cele 84 de camere scrise mai demult a rămas scris
 *  «Living»), iar pe ecranul englezesc scria «Dormitor» și «Balcon» — tocmai
 *  ce s-a cerut să nu se mai întâmple.
 *
 *  AȘA CĂ ETICHETA SE CITEȘTE, nu se scrie orbește. Trei forme se traduc, și
 *  toate trei sunt ale cititorului, nu ale omului:
 *    «Dormitor»            → chiar numele felului, în vreo limbă → se traduce
 *    «Dormitor 2»          → numele felului și un număr          → se traduce
 *    «Living + bucătărie»  → mai multe nume de feluri legate     → se traduc
 *  Orice altceva — «Spălătorie», «Hol lifturi», un nume pus de om — rămâne
 *  neatins: acolo eticheta spune ceva ce felul nu știe, și n-avem dreptul s-o
 *  pierdem ca să câștigăm o traducere. */
export function numeleCamerei(kind: string, eticheta?: string | null): string {
  const e = (eticheta ?? "").trim();
  if (!e) return numeleFelului(kind);
  // Felul AL CĂRUI NUME e textul ăsta — întâi al camerei, pe urmă oricare
  // altul: a doua baie a unui apartament e scrisă pe planșe când «Baie 2»
  // (felul `bath2`), când doar «Baie» (numele felului `bath`), și amândouă
  // spun ceva adevărat. Se traduce ce scrie, nu ce credem noi că e camera.
  const alCui = (nume: string) =>
    numeleCunoscute(kind).has(normal(nume)) ? kind : felulDupaNume(nume);
  const singur = alCui(e);
  if (singur) return numeleFelului(singur);
  const cu_numar = /^(.+?)\s*(\d+)$/.exec(e);
  const k_numar = cu_numar && alCui(cu_numar[1]);
  if (cu_numar && k_numar) {
    return `${numeleFelului(k_numar)} ${cu_numar[2]}`;
  }
  if (e.includes("+")) {
    const parti = e.split("+").map((x) => alCui(x));
    if (parti.length > 1 && parti.every(Boolean)) {
      return parti.map((k) => numeleFelului(k as string)).join(" + ");
    }
  }
  return e;
}
